// src/pages/PlayerProfile.js - PHASE 9: Simplified without usePlayerData hook
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { dynastyTheme } from '../services/colorService';
import api from '../services/apiService';
import PlayerInfoCard from '../components/player/PlayerInfoCard';

const PlayerProfile = () => {
  const { playerId, leagueId } = useParams();
  const navigate = useNavigate();
  
  // PHASE 9: Fetch only player data (no huge analytics)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [player, setPlayer] = useState(null);
  const [statsSeasonYear, setStatsSeasonYear] = useState(null);

  useEffect(() => {
    if (!playerId) return;

    const loadPlayerData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch player from /modal (includes analytics for InfoCard tiles)
        const response = leagueId
          ? await api.get(`/api/players/leagues/${leagueId}/players/${playerId}/modal`)
          : await api.get(`/api/players/${playerId}`);

        const data = response.data || response;
        const playerData = data.player;

        if (!playerData) {
          throw new Error('Invalid response structure - missing player data');
        }

        // Set player with canonical structure (modal includes analytics)
        setPlayer(playerData);
        setStatsSeasonYear(data.stats_season_year);

        console.log('✅ Player data loaded:', `${playerData.info?.first_name} ${playerData.info?.last_name}`);
      } catch (err) {
        console.error('❌ Error loading player data:', err);
        setError(err.message || 'Failed to load player data');
      } finally {
        setLoading(false);
      }
    };

    loadPlayerData();
  }, [playerId, leagueId]);

  // Helper to determine if player is pitcher
  const isPitcher = () => {
    const position = player?.info?.position?.toUpperCase();
    return ['P', 'SP', 'RP', 'CP', 'CL'].includes(position);
  };

  if (loading) {
    return (
      <div className={dynastyTheme.components.page}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
            <p className={dynastyTheme.classes.text.neutralLight}>Loading player data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || (!loading && !player)) {
    return (
      <div className={dynastyTheme.components.page}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className={dynastyTheme.classes.text.error}>Error: {error || 'Player not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    if (leagueId) {
      navigate(`/league/${leagueId}`);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className={dynastyTheme.components.pageWithPattern}>
      <div className="max-w-[1600px] mx-auto p-6">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={handleBack}
            className={`
              inline-flex items-center gap-2 px-4 py-2 rounded-lg
              ${dynastyTheme.classes.bg.darkLighter} 
              ${dynastyTheme.classes.text.neutralLight}
              hover:${dynastyTheme.classes.text.white}
              hover:${dynastyTheme.classes.bg.neutral}
              border ${dynastyTheme.classes.border.neutral}
              transition-all duration-200
            `}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to {leagueId ? 'League' : 'Dashboard'}</span>
          </button>
        </div>

        {/* PHASE 9: PlayerInfoCard is completely self-contained */}
        <PlayerInfoCard
          player={player}
          playerId={playerId}
          isPitcher={isPitcher()}
          leagueId={leagueId}
          statsSeasonYear={statsSeasonYear}
          onAddPlayer={(player) => {
            console.log('Add player:', player);
            // TODO: Implement actual add logic
          }}
          onDropPlayer={(player) => {
            console.log('Drop player:', player);
            // TODO: Implement actual drop logic
          }}
          onInitiateTrade={(player) => {
            console.log('Trade player:', player);
            // TODO: Implement actual trade logic
          }}
        />
        
        {/* NOTHING ELSE - NO DUPLICATE TABS, NO DUPLICATE OVERVIEW */}
      </div>
    </div>
  );
};

export default PlayerProfile;